import {
  findParentByMissionLink,
  isParentTasksAuthoritative as isParentTasksAuthoritativeFromInvariant,
  parentHasMissionLink,
} from '../../../utils/nestedCampaignInvariant';

const isTemplateItem = (item) => item?.isTemplate === true || item?.type === 'template';

export { isParentTasksAuthoritativeFromInvariant as isParentTasksAuthoritative };

/**
 * Whether any sprint references this id via a mission card (`nestedSprintId`).
 * childOrder is display order only — it must not invent a nest.
 */
export function isReferencedAsNested(childId, allSprints) {
  if (!childId) return false;
  for (const s of allSprints) {
    if (s.deletedAt) continue;
    if (parentHasMissionLink(s, childId)) return true;
  }
  return false;
}

/**
 * If this campaign sits under a trashed ancestor (parent chain), return that ancestor's id.
 * Used to hide "limbo" children instead of promoting them to root as orphans.
 */
export function getTrashedAncestorId(sprint, allSprints) {
  if (!sprint?.parentSprintId) return null;
  const byId = new Map(allSprints.map((s) => [s.id, s]));
  const seen = new Set();
  let pid = sprint.parentSprintId;

  while (pid && !seen.has(pid)) {
    seen.add(pid);
    const p = byId.get(pid);
    if (!p) return null;
    if (p.deletedAt) return p.id;
    pid = p.parentSprintId || null;
  }
  return null;
}

/**
 * Resolve the parent campaign for sidebar tree placement.
 * Flow shows a nest only when a mission has nestedSprintId. The sidebar uses
 * the same rule once the parent is fully loaded, so unlinked parentSprintId
 * rows cannot appear as nested campaigns without a mission card.
 *
 * Priority: mission link → parentSprintId only while the parent is still metadata.
 * childOrder is never a parent signal.
 */
export function getEffectiveParentId(sprint, allSprints) {
  if (!sprint) return null;

  const linkedParent = findParentByMissionLink(sprint.id, allSprints);
  if (linkedParent && !linkedParent.deletedAt) {
    return linkedParent.id;
  }

  if (sprint.parentSprintId) {
    const byId = new Map(allSprints.map((s) => [s.id, s]));
    const explicitParent = byId.get(sprint.parentSprintId);
    if (explicitParent && !explicitParent.deletedAt) {
      // Parent fully loaded and no mission card → not nested (unlinked ghost).
      if (isParentTasksAuthoritativeFromInvariant(explicitParent)) {
        return null;
      }
      return sprint.parentSprintId;
    }
  }

  return null;
}

/**
 * Classify a nested campaign relative to its parent.
 *
 * @returns {'valid'|'loading'|'orphan'|'hidden'|'unlinked'}
 */
export function classifyNestedCampaign(parent, nestedSprint, { deferOrphanDetection = false } = {}) {
  if (!parent) {
    return deferOrphanDetection ? 'loading' : 'orphan';
  }
  if (parent.deletedAt) {
    return 'hidden';
  }
  if (!isParentTasksAuthoritativeFromInvariant(parent)) {
    return deferOrphanDetection ? 'loading' : 'valid';
  }
  if (nestedSprint && parentHasMissionLink(parent, nestedSprint.id)) {
    return 'valid';
  }
  // Parent is fully loaded and has no mission containing this campaign.
  return 'unlinked';
}

/**
 * Find orphaned campaigns (parent truly missing).
 */
export function hasNestIntent(sprint, allSprints) {
  if (!sprint || sprint.deletedAt || isTemplateItem(sprint)) return false;
  return !!sprint.parentSprintId || isReferencedAsNested(sprint.id, allSprints);
}

function getPotentiallyNestedCampaigns(sprints) {
  return sprints.filter((s) => hasNestIntent(s, sprints));
}

function resolveClassificationParent(nestedSprint, sprints, lookup) {
  const linkedParent = findParentByMissionLink(nestedSprint.id, sprints);
  if (linkedParent) return linkedParent;
  if (nestedSprint.parentSprintId) {
    return lookup.get(nestedSprint.parentSprintId) || null;
  }
  return null;
}

export function detectOrphanedCampaigns(sprints, { deferOrphanDetection = false } = {}) {
  const lookup = new Map(sprints.map((s) => [s.id, s]));
  const potentiallyNested = getPotentiallyNestedCampaigns(sprints);

  const orphaned = [];
  const orphanedDetails = [];
  let isNestedDataLoading = false;

  for (const nestedSprint of potentiallyNested) {
    // Active child of a trashed ancestor: hide from sidebar (restored with parent).
    // Do NOT promote to root as an orphan.
    if (!nestedSprint.deletedAt && getTrashedAncestorId(nestedSprint, sprints)) {
      continue;
    }

    const parent = resolveClassificationParent(nestedSprint, sprints, lookup);
    const status = classifyNestedCampaign(parent, nestedSprint, { deferOrphanDetection });

    if (status === 'loading') {
      isNestedDataLoading = true;
      continue;
    }
    if (status === 'unlinked' || status === 'hidden') {
      continue;
    }
    if (status === 'orphan') {
      orphaned.push(nestedSprint);
      orphanedDetails.push({ sprint: nestedSprint, reason: 'missing_parent' });
    }
  }

  return { orphaned, orphanedDetails, isNestedDataLoading };
}

/**
 * Build the sidebar nested lists using the same classification rules.
 */
export function classifyAllNestedCampaigns(sprints, { deferOrphanDetection = false } = {}) {
  const lookup = new Map(sprints.map((s) => [s.id, s]));
  const potentiallyNested = getPotentiallyNestedCampaigns(sprints);

  const validNestedInCampaigns = [];
  const validNestedInTemplates = [];
  const orphaned = [];
  const orphanedDetails = [];
  let isNestedDataLoading = false;

  for (const nestedSprint of potentiallyNested) {
    // Active child of a trashed ancestor — hidden, not orphaned at root.
    if (!nestedSprint.deletedAt && getTrashedAncestorId(nestedSprint, sprints)) {
      continue;
    }

    const parent = resolveClassificationParent(nestedSprint, sprints, lookup);
    const status = classifyNestedCampaign(parent, nestedSprint, { deferOrphanDetection });

    if (status === 'hidden' || status === 'unlinked') continue;

    if (status === 'loading') {
      isNestedDataLoading = true;
      continue;
    }

    if (status === 'orphan') {
      orphaned.push(nestedSprint);
      orphanedDetails.push({ sprint: nestedSprint, reason: 'missing_parent' });
      continue;
    }

    // valid — nested under an existing, active parent that has a mission card
    // (or whose tasks are not loaded yet, so we keep the parentSprintId nest)
    if (isTemplateItem(parent)) {
      validNestedInTemplates.push(nestedSprint);
    } else {
      validNestedInCampaigns.push(nestedSprint);
    }
  }

  return {
    validNestedInCampaigns,
    validNestedInTemplates,
    orphaned,
    orphanedDetails,
    isNestedDataLoading,
  };
}

/**
 * A campaign is "truly orphaned" if it points at a parent that does not exist
 * anywhere in the data (not active, not in trash).
 */
export function isTrulyOrphaned(sprint, allSprints) {
  if (!sprint || sprint.deletedAt) return false;
  if (getTrashedAncestorId(sprint, allSprints)) return false;
  const nestIntent = sprint.parentSprintId || isReferencedAsNested(sprint.id, allSprints);
  if (!nestIntent) return false;
  if (findParentByMissionLink(sprint.id, allSprints)) return false;
  if (sprint.parentSprintId) {
    const parent = allSprints.find((s) => s.id === sprint.parentSprintId);
    if (parent) return false; // parent exists (even if this row is an unlinked ghost)
  }
  return getEffectiveParentId(sprint, allSprints) === null;
}

/**
 * Walk the parent chain upward and return the breadcrumb path the campaign
 * USED to live in. Stops at the first ancestor that can't be found — that
 * missing entry is the break point (great for debugging).
 *
 * @returns {Array<{ id: string, name: string|null, exists: boolean, deleted: boolean }>}
 *          ordered top-most ancestor → immediate parent
 */
export function getLostParentChain(sprint, allSprints) {
  const byId = new Map(allSprints.map((s) => [s.id, s]));
  const chain = [];
  const seen = new Set();
  let pid = sprint?.parentSprintId || null;

  while (pid && !seen.has(pid)) {
    seen.add(pid);
    const p = byId.get(pid);
    if (!p) {
      chain.unshift({ id: pid, name: null, exists: false, deleted: false });
      break; // break point reached
    }
    chain.unshift({ id: pid, name: p.name || pid, exists: true, deleted: !!p.deletedAt });
    pid = p.parentSprintId || null;
  }

  return chain;
}

export default detectOrphanedCampaigns;
